import type { paths } from '@forgd/openapi/api'
import type { UseMutationOptions } from '@pinia/colada'

import type {
  PathMethodParams,
  SuccessResponseBody,
  ValidMethods,
} from '../types/api'
import type { ApiError, QueryConfig } from './useApiFetch'
import { deepUnref } from '../utils/vue'
import { apiFetch } from './useApiFetch'
import { getApiQueryCacheKey } from './useApiQuery'

/**
 * Build a mutation options object that can be used with the useMutation hook from pinia colada
 *
 *  @example
 *  const { mutate } = useMutation(buildApiMutation('/projects/{projectId}/update', {
 *    method: 'PATCH',
 *  }))
 *
 *  mutate({
 *    path: {
 *      projectId: '123',
 *    },
 *    body: {
 *      name: 'New Project Name',
 *    },
 *  })
 */
export function buildApiMutation<P extends keyof paths, M extends ValidMethods<P>>(
  path: P,
  config?: Pick<QueryConfig<P, M>, 'method' | 'options'>,
): UseMutationOptions<SuccessResponseBody<P, M>, PathMethodParams<P, M>, ApiError, PathMethodParams<P, M>> {
  return {
    key: (params: PathMethodParams<P, M>) => getApiQueryCacheKey(path, { ...config, params: params as unknown } as never),
    mutation: async (params: QueryConfig<P, M>['params']) => {
      const parsedConfig = config ? deepUnref(config) : undefined

      return apiFetch<P, M>(path, { ...parsedConfig, params })
    },
  } as unknown as UseMutationOptions<SuccessResponseBody<P, M>, PathMethodParams<P, M>, ApiError, PathMethodParams<P, M>>
}
